/**
 * Wave speed arithmetic: v = fλ, the speed of a wave on a stretched string, and the
 * speed of sound in dry air. SI base units throughout (m/s, Hz, m, s, N, kg/m).
 */

export type WaveVariable = "v" | "f" | "lambda" | "T";

export interface WaveValues {
  /** Wave speed, m/s. */
  v: number;
  /** Frequency, Hz. */
  f: number;
  /** Wavelength, m. */
  lambda: number;
  /** Period, s. */
  T: number;
  /** Angular frequency 2πf, rad/s. */
  omega: number;
  /** Angular wavenumber 2π/λ, rad/m. */
  k: number;
}

function complete(v: number, f: number, lambda: number): WaveValues {
  return {
    v,
    f,
    lambda,
    T: 1 / f,
    omega: 2 * Math.PI * f,
    k: (2 * Math.PI) / lambda,
  };
}

/**
 * Solve v = fλ for one unknown. `speed`, `frequency` and `wavelength` hold whichever
 * knowns the form collected; a period is converted to a frequency before it gets here.
 * Returns null unless every value used is above zero.
 */
export function solveWave(
  target: "v" | "f" | "lambda",
  known: { v?: number; f?: number; lambda?: number }
): WaveValues | null {
  const { v, f, lambda } = known;
  if (target === "v" && f !== undefined && lambda !== undefined) {
    return f > 0 && lambda > 0 ? complete(f * lambda, f, lambda) : null;
  }
  if (target === "f" && v !== undefined && lambda !== undefined) {
    return v > 0 && lambda > 0 ? complete(v, v / lambda, lambda) : null;
  }
  if (target === "lambda" && v !== undefined && f !== undefined) {
    return v > 0 && f > 0 ? complete(v, f, v / f) : null;
  }
  return null;
}

/** Speed of a transverse wave on a string: v = √(F ÷ μ). */
export function stringSpeed(tension: number, density: number): number {
  return Math.sqrt(tension / density);
}

/** The tension that gives speed v on a string of linear density μ: F = μv². */
export function stringTension(v: number, density: number): number {
  return density * v * v;
}

/** The linear density that gives speed v under tension F: μ = F ÷ v². */
export function stringDensity(tension: number, v: number): number {
  return tension / (v * v);
}

export interface Harmonic {
  n: number;
  frequency: number;
  wavelength: number;
}

/** Standing waves of a string fixed at both ends: λₙ = 2L ÷ n, fₙ = n × v ÷ 2L. */
export function harmonics(
  v: number,
  length: number,
  count: number
): Harmonic[] {
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    return {
      n,
      frequency: (n * v) / (2 * length),
      wavelength: (2 * length) / n,
    };
  });
}

/** Specific gas constant of dry air, R/M, in J/(kg·K). */
export const R_SPECIFIC_AIR = 287.05;
/** Ratio of specific heats for air, treated as an ideal diatomic gas. */
export const GAMMA_AIR = 1.4;
const ZERO_CELSIUS = 273.15;

/** Speed of sound in dry air at a temperature in kelvin: √(γ × R × T), m/s. */
export function soundSpeedKelvin(kelvin: number): number {
  return Math.sqrt(GAMMA_AIR * R_SPECIFIC_AIR * kelvin);
}

/** The usual straight-line approximation, 331.3 + 0.606 θ (θ in °C), m/s. */
export function soundSpeedLinear(celsius: number): number {
  return 331.3 + 0.606 * celsius;
}

export type TemperatureUnit = "C" | "K" | "F";

/** A temperature in the chosen unit, as degrees Celsius. */
export function toCelsius(value: number, unit: TemperatureUnit): number {
  if (unit === "K") {
    return value - ZERO_CELSIUS;
  }
  if (unit === "F") {
    return ((value - 32) * 5) / 9;
  }
  return value;
}

export function celsiusToKelvin(celsius: number): number {
  return celsius + ZERO_CELSIUS;
}

/** Lowest temperature the formula is meaningful for: absolute zero. */
export const ABSOLUTE_ZERO_CELSIUS = -ZERO_CELSIUS;

export interface StringSolution {
  /** Wave speed, m/s. */
  v: number;
  /** Tension, N. */
  tension: number;
  /** Linear density, kg/m. */
  density: number;
}

/**
 * Solve v = √(F ÷ μ) for the chosen unknown. Returns "missing" while an input is
 * blank and "positive" when one is zero or negative.
 */
export function solveString(
  goal: "v" | "tension" | "density",
  known: { v: number | null; tension: number | null; density: number | null }
): StringSolution | "missing" | "positive" {
  const needs = {
    v: [known.tension, known.density],
    tension: [known.v, known.density],
    density: [known.tension, known.v],
  };
  const needed = needs[goal];
  if (needed.some((x) => x === null)) {
    return "missing";
  }
  if (needed.some((x) => (x ?? 0) <= 0)) {
    return "positive";
  }
  const { v, tension, density } = known;
  if (goal === "v") {
    return {
      v: stringSpeed(tension ?? 1, density ?? 1),
      tension: tension ?? 0,
      density: density ?? 0,
    };
  }
  if (goal === "tension") {
    return {
      v: v ?? 0,
      tension: stringTension(v ?? 0, density ?? 0),
      density: density ?? 0,
    };
  }
  return {
    v: v ?? 0,
    tension: tension ?? 0,
    density: stringDensity(tension ?? 0, v ?? 1),
  };
}
